import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Title from './Title';
import Paper from '@mui/material/Paper';
import axios from "axios";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Collapse from '@mui/material/Collapse';
import TableContainer from '@mui/material/TableContainer';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { useNavigate } from "react-router-dom";
import LinearProgress from '@mui/material/LinearProgress';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: "#e4e7eb",
        color: theme.palette.common.black,
        fontSize: 12,
        fontWeight: 600
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 12,
    },
}));

// Chip statusu subskrypcji (Active / Suspended / Canceled / Inne)
function StatusChip({ status }) {
    if (!status) return null;
    const s = String(status).toLowerCase().trim();

    let bg = '#e0e0e0';
    let fg = '#555';

    if (s === 'active') {
        bg = '#2e7d32';
        fg = '#fff';
    } else if (s === 'suspended' || s === 'onhold') {
        bg = '#ff9800';
        fg = '#fff';
    } else if (s === 'canceled' || s === 'cancelled' || s === 'terminated') {
        bg = '#d32f2f';
        fg = '#fff';
    }

    return (
        <Chip
            label={status}
            size="small"
            sx={{
                fontSize: 10,
                fontWeight: 600,
                height: 20,
                minWidth: 50,
                '& .MuiChip-label': {
                    px: 0.5,
                },
                backgroundColor: bg,
                color: fg,
                borderRadius: '10px',
            }}
        />
    );
}

// Ikonka dla aktywności (na TerminationDate można też patrzeć, ale zostawiamy prosty status)
function ActiveIcon({ status }) {
    const s = String(status || '').toLowerCase().trim();
    if (s === 'active') {
        return <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 18 }} />;
    }
    if (s === 'canceled' || s === 'cancelled' || s === 'terminated') {
        return <CancelIcon sx={{ color: '#d32f2f', fontSize: 18 }} />;
    }
    return <RemoveCircleOutlineIcon sx={{ color: '#b0b0b0', fontSize: 18 }} />;
}

function Row(props) {
    const { order } = props;
    const [open, setOpen] = React.useState(false);

    return (
        <React.Fragment>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                <StyledTableCell>
                    <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={() => setOpen(!open)}
                    >
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </StyledTableCell>
                <StyledTableCell>{order.Id}</StyledTableCell>
                <StyledTableCell sx={{ padding: '4px 8px' }}>
                    <StatusChip status={order.Status} />
                </StyledTableCell>
                <StyledTableCell>{order.CustomerName}</StyledTableCell>
                <StyledTableCell>{order.ProductName}</StyledTableCell>
                <StyledTableCell>{order.ProductGroup}</StyledTableCell>
                <StyledTableCell>{order.Quantity}</StyledTableCell>
                <StyledTableCell>{order.BillingPlan}</StyledTableCell>
                <StyledTableCell align="right">
                    {order.TerminationDate}
                </StyledTableCell>
            </TableRow>
            <TableRow>
                <StyledTableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Szczegóły subskrypcji
                            </Typography>
                            <Table size="small" aria-label="details">
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell>ParentId</StyledTableCell>
                                        <StyledTableCell>CustomerId</StyledTableCell>
                                        <StyledTableCell>ProductId</StyledTableCell>
                                        <StyledTableCell>BillingPlanId</StyledTableCell>
                                        <StyledTableCell>CreatedAt</StyledTableCell>
                                        <StyledTableCell>DBCreatedAt</StyledTableCell>
                                        <StyledTableCell>DBUpdatedAt</StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow key={`${order.CustomerId}-${order.ProductId}`}>
                                        <StyledTableCell component="th" scope="row">{order.ParentId}</StyledTableCell>
                                        <StyledTableCell>{order.CustomerId}</StyledTableCell>
                                        <StyledTableCell>{order.ProductId}</StyledTableCell>
                                        <StyledTableCell>{order.BillingPlanId}</StyledTableCell>
                                        <StyledTableCell>{order.CreatedAt}</StyledTableCell>
                                        <StyledTableCell>{order.DBCreatedAt}</StyledTableCell>
                                        <StyledTableCell>{order.DBUpdatedAt}</StyledTableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </StyledTableCell>
            </TableRow>
        </React.Fragment>
    );
}

export default function Subscriptions() {
    const [orders, setOrders] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [search, setSearch] = React.useState('');

    React.useEffect(() => {
        (async () => {
            try {
                const response = await axios.get("db/getsubscriptions/");
                setOrders(response.data || []);
            } catch (error) {
                console.log(error);
                if (error?.response?.status === 401) {
                    alert("Your access is not allowed.");
                }
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const navigate = useNavigate();
    const routeChange = () => {
        let path = "/orders";
        navigate(path);
    };

    // Wyszukiwarka subskrypcji – po kilku polach naraz (Customer, Product, Status...) [web:92]
    const filteredOrders = React.useMemo(() => {
        if (!search) return orders;
        const q = search.toLowerCase();
        return orders.filter(o => {
            return (
                (o.CustomerName && o.CustomerName.toLowerCase().includes(q)) ||
                (o.ProductName && o.ProductName.toLowerCase().includes(q)) ||
                (o.ProductGroup && o.ProductGroup.toLowerCase().includes(q)) ||
                (o.Status && o.Status.toLowerCase().includes(q)) ||
                (o.BillingPlan && o.BillingPlan.toLowerCase().includes(q)) ||
                (String(o.Id || '').toLowerCase().includes(q))
            );
        });
    }, [orders, search]);

    const renderOrdersTable = (ordersToRender) => {
        return (
            <React.Fragment>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Title>Subskrypcje (active/inactive)</Title>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <TextField
                                size="small"
                                variant="outlined"
                                label="Szukaj subskrypcji"
                                placeholder="Klient, produkt, status..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </Stack>
                    </Box>

                    <TableContainer>
                        <Table size="small" aria-label="collapsible table" padding="checkbox">
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell />
                                    <StyledTableCell>Id</StyledTableCell>
                                    <StyledTableCell>Status</StyledTableCell>
                                    <StyledTableCell>CustomerName</StyledTableCell>
                                    <StyledTableCell>ProductName</StyledTableCell>
                                    <StyledTableCell>ProductGroup</StyledTableCell>
                                    <StyledTableCell>Quantity</StyledTableCell>
                                    <StyledTableCell>Term/InvoicePeriod</StyledTableCell>
                                    <StyledTableCell align="right">TerminationDate</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {ordersToRender.map((order, index) => (
                                    <Row key={index} order={order} />
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {ordersToRender.length === 0 && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Brak subskrypcji spełniających kryteria wyszukiwania.
                            </Typography>
                        </Box>
                    )}
                </Paper>
            </React.Fragment>
        );
    };

    const contents = loading
        ? (
            <Box sx={{ width: '100%' }}>
                <LinearProgress />
                <p><em>&nbsp;&nbsp;Loading... trwa ładowanie danych - stay calm :) ...</em></p>
            </Box>
        )
        : renderOrdersTable(filteredOrders);

    return (
        <>
            {contents}
        </>
    );
}
